import { toQueryString } from "./WebUtils";

const DEBUG = false;

export const ENDPOINT_PATHS = {
    news: "news",
    configurations: "configurations",
};

export const ENDPOINTS = {
    listNews: "listNews",
    addNews: "addNews",
    getNews: "getNews",
    updateNews: "updateNews",
    patchNews: "patchNews",
    listNewsLocks: "listNewsLocks",
    acquireNewsLock: "acquireNewsLock",
    releaseNewsLock: "releaseNewsLock",
    getConfigurations: "getConfigurations",
};

export const ENDPOINT_METHODS = {
    listNews: "GET",
    addNews: "POST",
    getNews: "GET",
    updateNews: "PUT",
    patchNews: "PATCH",
    listNewsLocks: "GET",
    acquireNewsLock: "POST",
    releaseNewsLock: "DELETE",
    getConfigurations: "GET",
};

export const QUERYKEYS = ENDPOINTS;

// API publish_state values (OpenAPI enum)
export const PUBLISH_STATES = Object.freeze({
    NEW: "new",
    PUBLISHED: "published",
    DRAFT: "draft",
    REJECTED: "rejected",
    ERROR: "error",
});

export const ARTICLE_PAID_VALUES = Object.freeze([
    "Maksuton",
    "Maksullinen",
    "Rekisteröityneille",
]);

export const ARTICLE_SECTION_VALUES = Object.freeze([
    "Paikalliset",
    "Teemat",
    "Urheilu",
    "Uutissuomalainen",
]);

export const DEFAULT_ARTICLE_PAID_VALUE = ARTICLE_PAID_VALUES[0];
export const DEFAULT_ARTICLE_SECTION_VALUE = ARTICLE_SECTION_VALUES[0];

export function normalizeArticlePaidValue(value) {
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (ARTICLE_PAID_VALUES.includes(trimmed)) {
            return trimmed;
        }
    }

    // Backward compatibility for old boolean shape.
    if (value === true) return "Maksullinen";
    if (value === false) return "Maksuton";

    return DEFAULT_ARTICLE_PAID_VALUE;
}

export function normalizeArticleSectionValue(value) {
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (ARTICLE_SECTION_VALUES.includes(trimmed)) {
            return trimmed;
        }
    }

    return DEFAULT_ARTICLE_SECTION_VALUE;
}

function getNewsId(parameters) {
    if (!parameters) return null;
    return parameters.news_id ?? parameters.newsId ?? parameters.id ?? null;
}

function normalizeListNewsQuery(parameters) {
    if (!parameters) return {};

    // Backend expects: /news?target_media=Media1+Media2+Media3 (single string with + separators)
    // Accept various input formats for backwards compatibility.
    const targetMedia =
        parameters.target_media ?? parameters["target_media[]"] ?? parameters.targetMedia;

    // Accept boolean or string boolean from UI/form state.
    const showProcessedRaw = parameters.show_processed ?? parameters.showProcessed;
    const showProcessed =
        typeof showProcessedRaw === "string"
            ? showProcessedRaw.toLowerCase() === "true"
            : showProcessedRaw;

    const afterRaw = parameters.after ?? parameters.after_timestamp ?? parameters.afterTimestamp;
    const after = (() => {
        if (afterRaw == null) return null;
        if (afterRaw instanceof Date) return afterRaw.toISOString();
        const value = String(afterRaw).trim();
        return value.length > 0 ? value : null;
    })();

    const normalized = {};

    if (targetMedia != null) {
        // Convert array to + separated string for backend compatibility
        if (Array.isArray(targetMedia)) {
            normalized.target_media = targetMedia.join('+');
        } else if (typeof targetMedia === 'string') {
            // If it's already a string, use it as-is (could be + separated)
            normalized.target_media = targetMedia;
        } else {
            // Convert other types to string
            normalized.target_media = String(targetMedia);
        }
    }

    if (showProcessed != null) {
        normalized.show_processed = showProcessed;
    }

    if (after != null) {
        normalized.after = after;
    }

    return normalized;
}

export const createEndpointUrl = function ({ endpoint, parameters, body }) {
    if (DEBUG) console.log(`createEndpointUrl: ${endpoint}`);
    if (DEBUG && parameters) console.log(parameters);
    if (DEBUG && body) console.log(body);

    switch (endpoint) {
        case ENDPOINTS.listNews:
            {
                const query = normalizeListNewsQuery(parameters);
                const qs = toQueryString(query);
                return qs ? `${ENDPOINT_PATHS.news}?${qs}` : `${ENDPOINT_PATHS.news}`;
            }

        case ENDPOINTS.addNews:
            if (!body) {
                throw new Error(`Empty body given for ${endpoint}`);
            }
            return `${ENDPOINT_PATHS.news}`;

        case ENDPOINTS.getNews: {
            const newsId = getNewsId(parameters);
            if (!newsId) {
                throw new Error(`Required parameter 'news_id' is missing for ${endpoint}`);
            }
            return `${ENDPOINT_PATHS.news}/${newsId}`;
        }

        case ENDPOINTS.updateNews: {
            const newsId = getNewsId(parameters);
            if (!newsId) {
                throw new Error(`Required parameter 'news_id' is missing for ${endpoint}`);
            }
            if (!body) {
                throw new Error(`Empty body given for ${endpoint}`);
            }
            return `${ENDPOINT_PATHS.news}/${newsId}`;
        }

        case ENDPOINTS.patchNews: {
            const newsId = getNewsId(parameters);
            if (!newsId) {
                throw new Error(`Required parameter 'news_id' is missing for ${endpoint}`);
            }
            if (!body) {
                throw new Error(`Empty body given for ${endpoint}`);
            }
            return `${ENDPOINT_PATHS.news}/${newsId}`;
        }

        case ENDPOINTS.listNewsLocks:
            return `${ENDPOINT_PATHS.news}/locks`;

        case ENDPOINTS.acquireNewsLock: {
            const newsId = getNewsId(parameters);
            if (!newsId) {
                throw new Error(`Required parameter 'news_id' is missing for ${endpoint}`);
            }
            return `${ENDPOINT_PATHS.news}/${newsId}/lock`;
        }

        case ENDPOINTS.releaseNewsLock: {
            const newsId = getNewsId(parameters);
            if (!newsId) {
                throw new Error(`Required parameter 'news_id' is missing for ${endpoint}`);
            }
            return `${ENDPOINT_PATHS.news}/${newsId}/lock`;
        }

        case ENDPOINTS.getConfigurations:
            return `${ENDPOINT_PATHS.configurations}`;

        default:
            throw new Error(`Unknown endpoint: ${endpoint}`);
    }
};
